import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { LogIn } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { useAttendancePanel } from '../../hooks/workspace';
import { toast } from '../../store/toastStore';
import { apiErrorMessage } from '../sales/shared';
import { appLocale } from '../../utils/format';
import { formatClock, localDateKey } from '../home/homeModel';
import { clockInNudgeKey, needsClockIn } from '../documentation/documentationModel';
import { clockChoices, saveLocation, type ClockLocation } from './ClockInChoice';

function readHidden(key: string | null): boolean {
  if (!key) return false;
  try { return localStorage.getItem(key) === '1'; } catch { return false; }
}
function writeHidden(key: string) {
  try { localStorage.setItem(key, '1'); } catch { /* private mode: hidden for this visit only */ }
}

/**
 * "עוד לא החתמת כניסה היום" (documentation brief, part 1, 2026-10-12): a small
 * note under the top bar for an employee (admin / agent) with no attendance
 * event today. משרד / בית clock in right here (the same choice and order as
 * the top-bar `ClockInChoice`); "לא עכשיו" hides it until tomorrow, per user
 * and date in localStorage. It never blocks anything and sits in the page
 * flow, so it covers nothing.
 */
export default function ClockInNudge() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const userId = useAuthStore((s) => s.user?.id ?? null);
  const attendance = useAttendancePanel();
  const wrapRef = useRef<HTMLDivElement>(null);
  const storageKey = userId ? clockInNudgeKey(userId, localDateKey(new Date())) : null;
  const [hidden, setHidden] = useState(() => readHidden(storageKey));
  useEffect(() => { setHidden(readHidden(storageKey)); }, [storageKey]);

  if (!attendance.isAgentOrAdmin || !storageKey || hidden || !needsClockIn(attendance.status)) return null;

  const focusInside = () => !!wrapRef.current?.contains(document.activeElement);
  const choices = clockChoices();
  const locale = appLocale(i18n.language);

  const pick = (loc: ClockLocation) => {
    const refocus = focusInside();
    saveLocation(loc);
    attendance.clockIn(loc, {
      onSuccess: async (res) => {
        const at = typeof res?.data?.recorded_at === 'string' ? res.data.recorded_at : new Date().toISOString();
        toast('success', t('home.attendance.clockedInAt', { time: formatClock(at, locale) }));
        if (!refocus) return;
        // The note goes away with the new status: keep a keyboard user on the
        // attendance control, which now says "במשמרת".
        await queryClient.refetchQueries({ queryKey: ['attendance-status'] });
        requestAnimationFrame(() => {
          const target = document.querySelector<HTMLElement>('[data-attendance-control]') ?? document.getElementById('main');
          target?.focus({ preventScroll: true });
        });
      },
      onError: (err) => toast('error', apiErrorMessage(err, t('attendance.clockInFailed'))),
    });
  };

  const notNow = () => {
    const refocus = focusInside();
    writeHidden(storageKey);
    setHidden(true);
    if (refocus) document.getElementById('main')?.focus({ preventScroll: true });
  };

  return (
    <div ref={wrapRef} className="px-4 pt-3 sm:px-6 lg:px-8" data-clock-in-nudge="">
      {/* C-01 (clarity, D-01): one row on a wide screen; on a phone the line on
          top and the small משרד / בית and "לא עכשיו" under it (two rows, never a
          cut line); no filled button here, the top bar's "החתמת כניסה" is the blue one. */}
      <div className="flex flex-col gap-y-1.5 rounded-xl bg-accent-soft px-3 py-1.5 text-sm text-accent-soft-text sm:flex-row sm:items-center sm:gap-x-3">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <LogIn size={16} strokeWidth={1.75} className="rtl-mirror shrink-0" aria-hidden="true" />
          <p role="status" className="min-w-0 font-medium">{t('attendance.nudge.text')}</p>
        </div>
        <div className="flex items-center gap-1.5">
          <div role="group" aria-label={t('home.attendance.whereToday')} className="flex items-center gap-1.5">
            {choices.map(({ value, Icon }) => (
              <button
                key={value}
                type="button"
                onClick={() => pick(value)}
                disabled={attendance.isPending}
                className="btn btn-secondary btn-sm"
                data-clock-location={value}
              >
                <Icon size={14} strokeWidth={1.75} aria-hidden="true" />
                {t(`attendance.location_${value}`)}
              </button>
            ))}
          </div>
          <button type="button" onClick={notNow} className="btn btn-ghost btn-sm text-accent-soft-text">
            {t('attendance.nudge.notNow')}
          </button>
        </div>
      </div>
    </div>
  );
}
